import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button, Typography, Select, App, Grid, Flex } from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import { setFilters } from "./itemProfitLossSlice";
import { fetchItemProfitLoss } from "./itemProfitLossThunk";
import {
  fetchItems,
  fetchGroups,
  fetchCategories,
  fetchWarehouse,
} from "../../../store/commonThunks/lookups";

import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import ReportDom from "../../../components/common/ReportDom";
import ItemProfitLossTable from "./components/ItemProfitLossTable";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import { exportItemProfitLossCsv } from "./components/exportItemProfitLossCsv";

const { Title } = Typography;

const ItemProfitLossReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const reportRef = useRef();
  const { message } = App.useApp();

  const { filters, loading, data, totals } = useSelector(           // ← added data, totals
    (state) => state.itemProfitLoss
  );
  const { items, groups, categories, warehouses } = useSelector(
    (state) => state.lookups
  );

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const dateFrom = searchParams.get("dateFrom");
    const dateTo = searchParams.get("dateTo");

    if (dateFrom && dateTo) {
      const dateFilters = { fromDate: dateFrom, toDate: dateTo };
      dispatch(setFilters(dateFilters));
      dispatch(fetchItemProfitLoss({ ...filters, ...dateFilters }));
    }
  }, []);

  useEffect(() => {
    if (!items.length) dispatch(fetchItems());
    if (!groups.length) dispatch(fetchGroups());
    if (!categories.length) dispatch(fetchCategories());
    if (!warehouses.length) dispatch(fetchWarehouse());
  }, [dispatch]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.fromDate || !filters.toDate) {
      message.warning("Please select date range");
      return;
    }
    dispatch(fetchItemProfitLoss(filters));
  };

  const handleNavigateToBack = () => {
    navigate("/reports");
  };

  // ✅ ReactToPrint for consistent print
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Item Profit/Loss Report",
    removeAfterPrint: true,
  });
  const handleDownloadCsv = () => {
    exportItemProfitLossCsv({
      data,
      totals,
      filters,
      title:    "Item Profit/Loss Report",
      filename: "item_profit_loss_report",
    });
  };

  const hasData = data.length > 0;
  return (
    <div className="p-4">
      {/* Header */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }} className="reportBigTitle">
              Item Profit/Loss Report
            </Title>
          </div>
        }
   extra={
          // ← replaced single Button with Flex + two buttons
          <Flex gap={8}>
            {hasData && (
              <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
                {!screens.xs && "CSV"}
              </Button>
            )}
            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              {!screens.xs && "Print"}
            </Button>
          </Flex>
        }
      />

      {/* Filters */}
      <Card className="product-replace-filters shadow-md rounded-xl mb-4">
        <Row gutter={[16, 16]}>
          {/* Record Type */}
          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Record Type</label>
            <Select
              value={filters.type || "All Item Stock"}
              onChange={(val) => handleFilterChange("type", val)}
              style={{ width: "100%" }}
              options={[
                { value: "All Item Stock", label: "All Item Stock" },
                { value: "Item Wise", label: "Item Wise" },
                { value: "Group Wise", label: "Group Wise" },
                { value: "Category Wise", label: "Category Wise" },
                { value: "Warehouse Wise", label: "Warehouse Wise" },
              ]}
            />
          </Col>

          {/* Item Wise */}
          {filters.type === "Item Wise" && (
            <Col xs={24} sm={12} md={6} lg={4}>
              <label>Item</label>
              <SearchListAutoComplete
                optionsList={items.map((i) => ({
                  sale_id: i.item_id,
                  display_text: i.item_name,
                }))}
                onSelect={(obj) => handleFilterChange("itemId", obj?.sale_id)}
                placeholder="Select Item"
                showAddNew={false}
              />
            </Col>
          )}

          {/* Group Wise */}
          {filters.type === "Group Wise" && (
            <Col xs={24} sm={12} md={6} lg={4}>
              <label>Group</label>
              <SearchListAutoComplete
                optionsList={groups.map((g) => ({
                  sale_id: g.group_id,
                  display_text: g.group_name,
                }))}
                onSelect={(obj) => handleFilterChange("groupId", obj?.sale_id)}
                placeholder="Select Group"
                showAddNew={false}
              />
            </Col>
          )}

          {/* Category Wise */}
          {filters.type === "Category Wise" && (
            <Col xs={24} sm={12} md={6} lg={4}>
              <label>Category</label>
              <SearchListAutoComplete
                optionsList={categories.map((c) => ({
                  sale_id: c.category_id,
                  display_text: c.category_name,
                }))}
                onSelect={(obj) =>
                  handleFilterChange("categoryId", obj?.sale_id)
                }
                placeholder="Select Category"
                showAddNew={false}
              />
            </Col>
          )}

          {/* Warehouse Wise */}
          {filters.type === "Warehouse Wise" && (
            <Col xs={24} sm={12} md={6} lg={4}>
              <label>Warehouse</label>
              <SearchListAutoComplete
                optionsList={warehouses.map((w) => ({
                  sale_id: w.id,
                  display_text: w.name,
                }))}
                onSelect={(obj) =>
                  handleFilterChange("warehouseId", obj?.sale_id)
                }
                placeholder="Select Warehouse"
                showAddNew={false}
              />
            </Col>
          )}

          {/* Date Filters */}
          {/* <RecordDateFilter
            filters={filters}
            handleFilterChange={handleFilterChange}
          /> */}

          <CommonDateRangeFilter
            filters={filters}
            onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
            }}
          />

          {/* Report Button */}
          <Col
            xs={24}
            sm={12}
            md={6}
            lg={4}
            style={{
              display: "flex",
              alignItems: "flex-end", 
              height: 58
            }}
          >
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleReport}
              loading={loading}
              block
              style={{height: 35.18}}
            >
              Report
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Report Table */}
      <Card className="shadow-md rounded-xl">
        <ItemProfitLossTable />
      </Card>

      {/* Print Layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Item Profit/Loss Report"
          mode="report-page"
          filters={filters}
          renderTable={() => <ItemProfitLossTable printMode={true} />}
        />
      </div>
<style>{`
/* Highest specificity selector to override AntD grid */
.product-replace-filters :where(.ant-col) {
  min-width: 0 !important;
}

/* Fix overlapping: force full-width on tablet */
@media (min-width: 768px) and (max-width: 1400px) {
  .product-replace-filters :where(.ant-col) {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
}

/* Ensure autocomplete and select expand fully */
.product-replace-filters .ant-select,
.product-replace-filters input {
  width: 100% !important;
}`}
</style>
    </div>
  );
};

export default ItemProfitLossReport;
